/**
 * ISO 周字符串工具：``YYYY-Www`` ⇄ Date + helper for 2-digit padding.
 *
 * PR 9.2.1 B2: 加 ``formatIsoWeek`` / ``parseIsoWeek`` / ``compareIsoWeeksDesc``
 * 3 个 helper. ``formatWeek`` 自身也补零 (line 35: padStart). 4 caller 全栈对齐.
 */

/**
 * Parse "2026-W19" → 该周周一 Date 对象（UTC 00:00）。返回 null 表示格式错误。
 * 接受 1 位 (W9) 和 2 位 (W09), backward compat.
 */
export function parseWeekMonday(week: string): Date | null {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(week);
  if (!m) return null;
  const year = Number(m[1]);
  const weekNo = Number(m[2]);
  // ISO week 1 = the week containing the year's first Thursday.
  // Algorithm: take Jan 4 (always in week 1), find its Monday, then add (weekNo - 1) * 7 days.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7; // Sun=0→7
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  const target = new Date(week1Monday);
  target.setUTCDate(week1Monday.getUTCDate() + (weekNo - 1) * 7);
  return target;
}

/**
 * Format Date → "2026-W09" (using ISO calendar, **2-digit padded** since PR 9.2.1 B2).
 *
 * Pre-fix: 输出 ``2026-W9`` (无补零), lex 排序下 ``W10 < W9`` bug. 修复后
 * 跟其它 caller (run_week._current_iso_week / weekStore / formatIsoWeek) 对齐.
 */
export function formatWeek(d: Date): string {
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayOfWeek = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayOfWeek);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return formatIsoWeek(target.getUTCFullYear(), weekNo);
}

// ============================================================================
// PR 9.2.1 B2: ISO 周字符串 helper (2-digit padded + numeric compare)
// ============================================================================

/**
 * ``formatIsoWeek(2026, 9)`` → ``"2026-W09"``. 周号补零到 2 位.
 *
 * Pre-fix 4 caller 各自手写 ``\`${year}-W${week}\``` (无补零). 统一走此
 * helper 后所有输出对齐.
 */
export function formatIsoWeek(year: number, week: number): string {
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * ``parseIsoWeek("2026-W09")`` → ``[2026, 9]``. 接受 1 位 (W9, backward
 * compat 对历史 stale 数据) 和 2 位 (W09). 格式错抛 ``Error``.
 */
export function parseIsoWeek(s: string): [number, number] {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(s);
  if (!m) throw new Error(`invalid ISO week string: ${s}`);
  return [Number(m[1]), Number(m[2])];
}

/**
 * 数值降序比较 (后期周排前面). 用于 ``[...weeks].sort(compareIsoWeeksDesc)``.
 *
 * Pre-fix lex 排序 regression: ``["2026-W9", "2026-W10"].sort()`` →
 * ``["2026-W10", "2026-W9"]`` (W10 在前但只因字符串比较 '1' < '9'),
 * 数值上应该是 W10 > W9 → W10 在前 — 这例巧合对. **真错的** case:
 * ``["2025-W52", "2026-W9"].sort()`` 数值应给 W9-2026 先 (2026 > 2025)
 * 但 lex 给 W52-2025 先 ('2025' < '2026'). 用本函数则不出错.
 */
export function compareIsoWeeksDesc(a: string, b: string): number {
  const [ay, aw] = parseIsoWeek(a);
  const [by, bw] = parseIsoWeek(b);
  if (ay !== by) return by - ay;
  return bw - aw;
}
